/**
 * Escapes special XML characters in a string
 * @param {string} str - String to escape
 * @returns {string} XML-escaped string
 */
export const escapeXML = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

/**
 * Generates KML content for a route with stations, path, and campuses
 * @param {string} routeName - Name of the route
 * @param {Array} stations - Array of station objects with location data
 * @param {Array} path - Array of coordinates {lat, lng} for the route path
 * @param {Array} campuses - Array of campus objects with location data
 * @returns {string} KML XML content
 */
export const generateKML = (routeName, stations, path, campuses = []) => {
  const campusPlacemarks = campuses
    .map(
      (campus) => `
    <Placemark>
      <name>${escapeXML(campus.name || 'Campus')}</name>
      <description>Campus</description>
      <Point>
        <coordinates>${campus.location?.longitude},${campus.location?.latitude},0</coordinates>
      </Point>
    </Placemark>`,
    )
    .join('');

  const stationPlacemarks = stations
    .map(
      (station) => `
    <Placemark>
      <name>${escapeXML(station.description || station.name || 'Station')}</name>
      <description>Station ID: ${station.id}</description>
      <Point>
        <coordinates>${station.location?.longitude},${station.location?.latitude},0</coordinates>
      </Point>
    </Placemark>`,
    )
    .join('');

  const pathCoordinates = path.map((point) => `${point.lng},${point.lat},0`).join(' ');

  const pathPlacemark =
    path.length > 0
      ? `
    <Placemark>
      <name>Route Path</name>
      <Style>
        <LineStyle>
          <color>ff0000ff</color>
          <width>4</width>
        </LineStyle>
      </Style>
      <LineString>
        <coordinates>
          ${pathCoordinates}
        </coordinates>
      </LineString>
    </Placemark>`
      : '';

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXML(routeName)}</name>
    <description>Route export from flexigo</description>${campusPlacemarks}${stationPlacemarks}${pathPlacemark}
  </Document>
</kml>`;
};

/**
 * Generates a Google Maps Directions URL for a route
 * @param {Object} options - Configuration object
 * @param {string} options.origin - Origin coordinates (lat,lng) or place name (required)
 * @param {string} options.destination - Destination coordinates (lat,lng) or place name (required)
 * @param {string} options.waypoints - Pipe-separated waypoint coordinates (lat,lng pairs)
 * @param {string} options.travelmode - Travel mode: 'driving', 'walking', 'bicycling', or 'transit' (default: 'driving')
 * @param {string} options.dir_action - Direction action: 'navigate' for turn-by-turn navigation
 * @param {string} options.origin_place_id - Place ID for origin
 * @param {string} options.destination_place_id - Place ID for destination
 * @param {string} options.waypoint_place_ids - Pipe-separated place IDs for waypoints
 * @returns {string} Google Maps Directions URL
 */
export const generateGoogleMapsDirectionsUrl = (options = {}) => {
  // Validate required parameters
  if (!options.origin || !options.destination) {
    return '';
  }

  const baseUrl = 'https://www.google.com/maps/dir/?api=1';
  const params = new URLSearchParams();

  // Set required parameters
  params.append('origin', options.origin);
  params.append('destination', options.destination);

  // Set travel mode (default: driving)
  params.append('travelmode', options.travelmode || 'driving');

  // Add optional parameters if provided
  if (options.waypoints) params.append('waypoints', options.waypoints);
  if (options.dir_action) params.append('dir_action', options.dir_action);
  if (options.origin_place_id) params.append('origin_place_id', options.origin_place_id);
  if (options.destination_place_id) params.append('destination_place_id', options.destination_place_id);
  if (options.waypoint_place_ids) params.append('waypoint_place_ids', options.waypoint_place_ids);

  return `${baseUrl}&${params.toString()}`;
};

/**
 * Finds a matching station in a list based on physical location (latitude/longitude)
 * Station IDs are unique per route, so we match by exact coordinates instead
 *
 * @param {Object} sourceStation - The station to find a match for
 * @param {Object} sourceStation.location - Location object with latitude and longitude
 * @param {number} sourceStation.location.latitude - Latitude coordinate
 * @param {number} sourceStation.location.longitude - Longitude coordinate
 * @param {Array} targetStationsList - Array of stations to search within
 * @param {number} epsilon - Maximum allowed difference for coordinate comparison (default: 1e-9)
 * @returns {Object|null} The matching station or null if not found
 */
export const findMatchingStationByLocation = (sourceStation, targetStationsList, epsilon = 1e-9) => {
  if (!sourceStation?.location || !targetStationsList) return null;
  const { latitude: srcLat, longitude: srcLng } = sourceStation.location;
  if (srcLat == null || srcLng == null) return null;

  return targetStationsList.find((station) => {
    const { latitude: tgtLat, longitude: tgtLng } = station.location || {};
    if (tgtLat == null || tgtLng == null) return false;

    // Use epsilon-based comparison for floating-point coordinates
    // Two coordinates are considered equal if their difference is less than epsilon
    const latMatch = Math.abs(tgtLat - srcLat) < epsilon;
    const lngMatch = Math.abs(tgtLng - srcLng) < epsilon;

    return latMatch && lngMatch;
  });
};

/**
 * Convert a ring of `[lat, lng]` pairs into Google Maps `{ lat, lng }` objects.
 * @param {Array<[number, number]>} ring
 * @returns {Array<{lat: number, lng: number}>}
 */
export const ringToLatLng = (ring = []) => ring.map(([lat, lng]) => ({ lat, lng }));

/**
 * Signed area of a ring of `{lat, lng}` points (shoelace formula). The sign
 * encodes the winding direction (clockwise vs counter-clockwise).
 * @param {Array<{lat: number, lng: number}>} ring
 * @returns {number}
 */
const ringSignedArea = (ring) => {
  let area = 0;
  for (let i = 0, n = ring.length; i < n; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    area += a.lng * b.lat - b.lng * a.lat;
  }
  return area / 2;
};

/**
 * Build a `<gmap-polygon :paths>` value from a region's outer ring plus its hole
 * rings (exclusions). Returns `[outerRing, ...holeRings]`.
 *
 * Google Maps only renders an inner ring as a HOLE when it is wound in the
 * OPPOSITE direction to the outer ring; a hole wound the same way is filled in
 * instead of cut out. Backends (and KML) commonly store every ring with the same
 * winding, so each hole that shares the outer ring's winding is reversed here.
 * Safe when `exclusions` is empty/undefined (then it is just a single-ring polygon).
 * @param {Array<[number, number]>} coordinates - outer ring, `[lat, lng]` pairs
 * @param {Array<Array<[number, number]>>} [exclusions] - hole rings
 * @returns {Array<Array<{lat: number, lng: number}>>}
 */
export const regionPolygonPaths = (coordinates = [], exclusions = []) => {
  const outer = ringToLatLng(coordinates);
  const outerSign = Math.sign(ringSignedArea(outer));
  const holes = (exclusions || []).map((ring) => {
    const hole = ringToLatLng(ring);
    if (outerSign !== 0 && Math.sign(ringSignedArea(hole)) === outerSign) hole.reverse();
    return hole;
  });
  return [outer, ...holes];
};
